import {normalizeMedicationScheduleQuantity,buildMedicationDetailSummary} from '../../../features/medications/medicationDosePresentation';
import { readCanonicalMedicationRecord } from '../../../features/medications/canonicalMedicationRecord';

export const MEDICATION_CATEGORY_OPTIONS = [
  { value: "prescription", label: "Prescription" },
  { value: "otc", label: "OTC" },
  { value: "supplement", label: "Supplement" },
  { value: "vitamin", label: "Vitamin" },
  { value: "prn", label: "PRN" },
  { value: "other", label: "Other" },
];

export const MEDICATION_FORM_OPTIONS = [
  { value: "pill", label: "Pill", icon: "pill", defaultRoute: "oral" },
  { value: "tablet", label: "Tablet", icon: "tablet", defaultRoute: "oral" },
  { value: "capsule", label: "Capsule", icon: "capsule", defaultRoute: "oral" },
  { value: "liquid", label: "Liquid", icon: "liquid", defaultRoute: "oral" },
  { value: "drops", label: "Drops", icon: "drops", defaultRoute: "eye" },
  { value: "cream", label: "Cream", icon: "cream", defaultRoute: "topical" },
  { value: "suppository", label: "Suppository", icon: "suppository", defaultRoute: "rectal" },
  { value: "inhaler", label: "Inhaler", icon: "inhaler", defaultRoute: "inhaled" },
];

export const MEDICATION_FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "twice_daily", label: "Twice daily" },
  { value: "three_times_daily", label: "Three times daily" },
  { value: "weekly", label: "Weekly" },
  { value: "as_needed", label: "As needed" },
  { value: "every_x_hours", label: "Every X hours" },
  { value: "custom", label: "Custom" },
];

export const MEDICATION_FOOD_OPTIONS = [
  { value: "before_food", label: "Before food" },
  { value: "with_food", label: "With food" },
  { value: "anytime", label: "Anytime" },
];

export const MEDICATION_TIMING_OPTIONS = [
  { value: "morning", label: "Morning", hint: "8:00" },
  { value: "afternoon", label: "Afternoon", hint: "1:00" },
  { value: "evening", label: "Evening", hint: "8:00" },
  { value: "bedtime", label: "Bedtime", hint: "10:00" },
];

export const MEDICATION_ROUTE_OPTIONS = [
  { value: "oral", label: "Oral" },
  { value: "rectal", label: "Rectal" },
  { value: "vaginal", label: "Vaginal" },
  { value: "topical", label: "Topical" },
  { value: "eye", label: "Eyes" },
  { value: "ear", label: "Ears" },
  { value: "nose", label: "Nose" },
  { value: "inhaled", label: "Inhaled" },
  { value: "subcutaneous", label: "Injection" },
];

/**
 * @typedef {Object} MedicationSchedule
 * @property {string} id
 * @property {string} dose
 * @property {string} unit
 * @property {string} time
 */

/**
 * @typedef {Object} MedicationVersion
 * @property {string} id
 * @property {string} name
 * @property {string} dose
 * @property {string} unit
 * @property {string} time
 * @property {string} form
 * @property {string} category
 * @property {MedicationSchedule[]} schedules
 * @property {string} startDate
 * @property {boolean} isArchived
 * @property {string} archivedAt
 * @property {string} archivedBy
 * @property {string} effectiveFrom
 * @property {string} effectiveTo
 * @property {MedicationVersion[]} history
 */

/**
 * @typedef {Object} MedicationDoseResolution
 * @property {boolean} resolved
 * @property {string|null} reason
 * @property {MedicationVersion|null} version
 * @property {MedicationSchedule|null} schedule
 * @property {string} medicationId
 * @property {string} medicationName
 * @property {string} plannedDose
 * @property {string} unit
 * @property {string} scheduledTime
 * @property {string} effectiveFrom
 * @property {string} effectiveTo
 * @property {string} medicationVersionId
 * @property {string|null} medicationScheduleId
 * @property {Object} versionSnapshot
 * @property {Object} scheduleSnapshot
 */

/**
 * @typedef {Object} MedicationLogEntry
 * @property {string} childId
 * @property {string} medicationId
 * @property {string} medicationName
 * @property {string} medicationVersionId
 * @property {Object} medicationVersionSnapshot
 * @property {string|null} medicationScheduleId
 * @property {Object} medicationScheduleSnapshot
 * @property {string} medicationScheduleTime
 * @property {string} medicationScheduleDose
 * @property {string} medicationScheduleUnit
 * @property {string} plannedDose
 * @property {string} actualDose
 * @property {string} unit
 * @property {string} status
 * @property {string} entryDate
 * @property {string} actualDateTime
 * @property {string|null} scheduledFor
 * @property {string|null} takenAt
 */

export const getMedicationDefaultRoute = (form) => {
  const formOption = MEDICATION_FORM_OPTIONS.find((option) => option.value === form);
  return formOption?.defaultRoute || "oral";
};

export const shouldShowMedicationRoute = (form) => {
  if (!form) {
    return false;
  }

  return ["suppository", "drops"].includes(form);
};

export const getMedicationRouteOptions = (form) => {
  if (form === "suppository") {
    return ["rectal", "vaginal"];
  }

  if (form === "drops") {
    return ["eye", "ear", "nose"];
  }

  return MEDICATION_ROUTE_OPTIONS.map((option) => option.value);
};

const getCurrentLocalTime = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const parseLocalDateKey = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    const localDateOnlyMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (localDateOnlyMatch) {
      const [, year, month, day] = localDateOnlyMatch;
      const localDate = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
      if (!Number.isNaN(localDate.getTime())) {
        return [
          localDate.getFullYear(),
          String(localDate.getMonth() + 1).padStart(2, "0"),
          String(localDate.getDate()).padStart(2, "0"),
        ].join("-");
      }
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const coerceDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === "function") {
    const resolved = value.toDate();
    return resolved instanceof Date && !Number.isNaN(resolved.getTime()) ? resolved : null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseTimeMinutes = (value = "") => {
  const text = String(value || "").trim();
  if (!text) {
    return null;
  }

  const parts = text.split(":").map((part) => Number(part));
  if (parts.length < 2 || parts.some((part) => Number.isNaN(part))) {
    return null;
  }

  return parts[0] * 60 + parts[1];
};

const getLocalTimeKey = (value) => {
  const date = coerceDate(value);
  if (!date) {
    return "";
  }

  return [
    String(date.getHours()).padStart(2, "0"),
    String(date.getMinutes()).padStart(2, "0"),
  ].join(":");
};

const getVersionSortKey = (version = {}) => (
  parseLocalDateKey(version.effectiveFrom || version.startDate || version.savedAt || version.createdAt || "")
  || "0000-00-00"
);

const getVersionEndKey = (version = {}) => (
  parseLocalDateKey(version.effectiveTo || version.archivedAt || "")
);

const isVersionActiveAtDateKey = (version = {}, dateKey = "") => {
  if (!dateKey) {
    return true;
  }

  const startKey = getVersionSortKey(version);
  const endKey = getVersionEndKey(version);

  if (startKey && dateKey < startKey) {
    return false;
  }

  if (endKey && dateKey > endKey) {
    return false;
  }

  return true;
};

const resolveScheduleForTimestamp = (schedules = [], timestamp = new Date()) => {
  const normalizedSchedules = Array.isArray(schedules) ? schedules.filter(Boolean) : [];
  if (!normalizedSchedules.length) {
    return null;
  }

  if (normalizedSchedules.length === 1) {
    return { schedule: normalizedSchedules[0], reason: 'single_schedule' };
  }

  const timeKey = getLocalTimeKey(timestamp);
  if (timeKey) {
    const exactMatch = normalizedSchedules.find((schedule) => String(schedule.time || "").trim() === timeKey);
    if (exactMatch) {
      return { schedule: exactMatch, reason: 'exact_match' };
    }
  }

  const targetMinutes = parseTimeMinutes(timeKey);
  if (targetMinutes == null) {
    return { schedule: normalizedSchedules[0], reason: 'fallback_first' };
  }

  const scoredSchedules = normalizedSchedules
    .map((schedule, index) => {
      const minutes = parseTimeMinutes(schedule.time);
      if (minutes == null) {
        return null;
      }

      return {
        schedule,
        index,
        delta: Math.abs(minutes - targetMinutes),
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.delta - right.delta || left.index - right.index);

  if (!scoredSchedules.length) {
    return { schedule: normalizedSchedules[0], reason: 'fallback_first' };
  }

  return {
    schedule: scoredSchedules[0].schedule,
    reason: scoredSchedules[0].delta === 0 ? 'exact_match' : 'nearest_match',
  };
};

const getDoseReviewPlanHistoryEntries = (medication = {}) => {
  const planHistory = Array.isArray(medication.planHistory) ? medication.planHistory : [];

  return planHistory
    .filter((entry) => {
      const reviewReasons = Array.isArray(entry?.reviewReasons) ? entry.reviewReasons : [];
      const hasDoseDetail = Boolean(entry?.dose || entry?.previousDose || entry?.newDose);
      const needsDateReview = Boolean(
        entry?.dateApproximate
        || entry?.needsReview
        || reviewReasons.includes("exact_date_needed")
      );

      return hasDoseDetail && needsDateReview;
    })
    .map((entry) => ({
      id: String(entry.id || "").trim(),
      type: String(entry.type || "plan_change").trim(),
      approximateDateText: String(entry.approximateDateText || "").trim(),
      dateApproximate: Boolean(entry.dateApproximate),
      previousDose: String(entry.previousDose || "").trim(),
      previousUnit: String(entry.previousUnit || "").trim(),
      newDose: String(entry.newDose || entry.dose || "").trim(),
      newUnit: String(entry.newUnit || entry.unit || "").trim(),
      schedule: String(entry.schedule || "").trim(),
      reason: String(entry.reason || "").trim(),
      needsReview: true,
      reviewReasons: Array.isArray(entry.reviewReasons) && entry.reviewReasons.length
        ? entry.reviewReasons
        : ["exact_date_needed"],
      sourceId: String(entry.sourceId || entry.sourceDocument?.sourceId || "").trim(),
      sourceTitle: String(entry.sourceTitle || entry.sourceDocument?.title || "").trim(),
    }));
};

const buildDoseResolutionPlanHistoryReview = (medication = {}) => {
  const entries = getDoseReviewPlanHistoryEntries(medication);
  if (!entries.length) {
    return null;
  }

  return {
    reason: "approximate_medication_plan_history",
    entries,
  };
};

export const getActiveMedicationVersionAtTimestamp = (medication = {}, timestamp = new Date()) => {
  const normalized = normalizeMedicationDetail(medication);
  const dateKey = parseLocalDateKey(timestamp || new Date());
  const versions = [
    normalized,
    ...(Array.isArray(normalized.history) ? normalized.history : []),
  ]
    .filter(Boolean)
    .map((version, index) => normalizeMedicationDetail(version, `${normalized.id || "med"}-version-${index}`));

  const activeVersions = versions.filter((version) => isVersionActiveAtDateKey(version, dateKey));
  if (!activeVersions.length) {
    return normalized;
  }

  activeVersions.sort((left, right) => {
    const leftKey = getVersionSortKey(left);
    const rightKey = getVersionSortKey(right);
    if (leftKey !== rightKey) {
      return leftKey.localeCompare(rightKey);
    }

    return String(left.savedAt || left.createdAt || "").localeCompare(String(right.savedAt || right.createdAt || ""));
  });

  return activeVersions[activeVersions.length - 1];
};

export const getActiveDoseAtTime = (medicationId, timestamp, medications = []) => {
  const medicationList = Array.isArray(medications) ? medications : Object.values(medications || {});
  const medication = typeof medicationId === "object" && medicationId
    ? medicationId
    : medicationList.find((entry) => entry?.id === medicationId) || null;

  if (!medication) {
    return {
      resolved: false,
      reason: "missing_medication",
      version: null,
      schedule: null,
      medicationId: medicationId || "",
      medicationName: "",
      plannedDose: "",
      unit: "",
      scheduledTime: "",
      effectiveFrom: "",
      effectiveTo: "",
      medicationVersionId: "",
      medicationScheduleId: null,
      versionSnapshot: null,
      scheduleSnapshot: null,
    };
  }

  const version = getActiveMedicationVersionAtTimestamp(medication, timestamp);
  const versionSnapshot = buildMedicationVersionSnapshot(version);
  const { schedule, reason } = resolveScheduleForTimestamp(version.schedules, timestamp);
  const planHistoryReview = buildDoseResolutionPlanHistoryReview(version);
  const reviewReasons = planHistoryReview ? ["approximate_medication_plan_history"] : [];
  const scheduleSnapshot = schedule
    ? normalizeMedicationSchedule(schedule, schedule.id || `${version.id}-schedule`)
    : null;

  if (!scheduleSnapshot) {
    return {
      resolved: false,
      reason: "missing_schedule_history",
      version,
      schedule: null,
      medicationId: version.id,
      medicationName: version.name || "",
      plannedDose: version.dose || "",
      unit: version.unit || "",
      scheduledTime: "",
      effectiveFrom: version.effectiveFrom || version.startDate || "",
      effectiveTo: version.effectiveTo || "",
      medicationVersionId: version.id,
      medicationScheduleId: scheduleSnapshot?.id || null,
      versionSnapshot,
      scheduleSnapshot: null,
      needsReview: Boolean(planHistoryReview),
      reviewReasons,
      planHistoryReview,
    };
  }

  return {
    resolved: true,
    reason,
    version,
    schedule: scheduleSnapshot,
    medicationId: version.id,
    medicationName: version.name || "",
    plannedDose: scheduleSnapshot.dose || version.dose || "",
    unit: scheduleSnapshot.unit || version.unit || "",
    scheduledTime: scheduleSnapshot.time || "",
    effectiveFrom: version.effectiveFrom || version.startDate || "",
    effectiveTo: version.effectiveTo || "",
    medicationVersionId: version.id,
    medicationScheduleId: scheduleSnapshot?.id || null,
    versionSnapshot,
    scheduleSnapshot,
    needsReview: Boolean(planHistoryReview),
    reviewReasons,
    planHistoryReview,
  };
};

export const createMedicationLogEntry = ({
  childId = "",
  medication = null,
  medicationId = "",
  medications = [],
  timestamp = new Date(),
  actualTime = null,
  actualDose = "",
  status = "taken",
  note = "",
  authorId = "",
  authorName = "",
  authorEmail = "",
} = {}) => {
  const activeDose = getActiveDoseAtTime(medication || medicationId, timestamp, medications);
  const resolvedTimestamp = coerceDate(timestamp) || new Date();
  const resolvedActualTime = actualTime ? coerceDate(actualTime) : resolvedTimestamp;
  const plannedDose = activeDose?.plannedDose || "";
  const unit = activeDose?.unit || "";
  const scheduleSnapshot = activeDose?.scheduleSnapshot || null;
  const versionSnapshot = activeDose?.versionSnapshot || null;
  const actualDoseValue = status === "taken"
    ? (actualDose || plannedDose)
    : (actualDose || "");
  const doseValue = status === "taken"
    ? (actualDoseValue || plannedDose)
    : (plannedDose || actualDoseValue);

  return {
    childId,
    medicationId: activeDose?.medicationId || medicationId || "",
    medicationName: activeDose?.medicationName || normalizeMedicationDetail(medication || {}).name || "",
    medicationVersionId: activeDose?.medicationVersionId || "",
    medicationVersionSnapshot: versionSnapshot,
    medicationScheduleId: activeDose?.schedule?.id || null,
    medicationScheduleSnapshot: scheduleSnapshot,
    medicationScheduleTime: activeDose?.scheduledTime || "",
    medicationScheduleDose: plannedDose,
    medicationScheduleUnit: unit,
    plannedDose,
    actualDose: actualDoseValue,
    dose: doseValue,
    unit,
    status,
    entryDate: parseLocalDateKey(resolvedTimestamp),
    scheduledDate: parseLocalDateKey(resolvedTimestamp),
    scheduledTime: activeDose?.scheduledTime || "",
    actualDateTime: resolvedActualTime.toISOString(),
    scheduledFor: resolvedTimestamp.toISOString(),
    takenAt: status === "taken" ? resolvedActualTime.toISOString() : null,
    note: note || null,
    authorId,
    authorName,
    authorEmail,
    medicationDoseResolution: {
      resolved: Boolean(activeDose?.resolved),
      reason: activeDose?.reason || null,
      medicationId: activeDose?.medicationId || medicationId || "",
      medicationName: activeDose?.medicationName || "",
      plannedDose,
      unit,
      scheduledTime: activeDose?.scheduledTime || "",
      effectiveFrom: activeDose?.effectiveFrom || "",
      effectiveTo: activeDose?.effectiveTo || "",
      medicationVersionId: activeDose?.medicationVersionId || "",
      needsReview: Boolean(activeDose?.needsReview),
      reviewReasons: activeDose?.reviewReasons || [],
      planHistoryReview: activeDose?.planHistoryReview || null,
    },
  };
};

export const updateMedicationDose = ({
  medication = null,
  medicationId = "",
  nextDose = "",
  nextUnit = "",
  nextSchedules = null,
  effectiveFrom = "",
  closePreviousVersion = true,
  createdBy = "",
} = {}) => {
  const current = normalizeMedicationDetail(medication || { id: medicationId });
  const savedAt = new Date().toISOString();
  const currentSnapshot = buildMedicationVersionSnapshot(current);
  const currentHistory = Array.isArray(current.history) ? current.history : [];
  const resolvedSchedules = Array.isArray(nextSchedules) && nextSchedules.length
    ? nextSchedules.map((schedule, index) => normalizeMedicationSchedule(schedule, `${current.id || "med"}-${index}`))
    : current.schedules;

  const nextRecord = {
    ...current,
    id: current.id || medicationId || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    dose: String(nextDose || current.dose || "").trim(),
    unit: String(nextUnit || current.unit || "mg").trim() || "mg",
    schedules: resolvedSchedules.map((schedule, index) => normalizeMedicationSchedule(schedule, `${current.id || medicationId || "med"}-${index}`)),
    effectiveFrom: effectiveFrom || current.effectiveFrom || current.startDate || savedAt,
    effectiveTo: current.effectiveTo || "",
    history: closePreviousVersion
      ? [...currentHistory, {
          ...currentSnapshot,
          effectiveTo: currentSnapshot.effectiveTo || savedAt,
        }]
      : currentHistory,
    createdBy: current.createdBy || createdBy || "",
    createdAt: current.createdAt || savedAt,
    savedAt,
    updatedAt: savedAt,
    isArchived: Boolean(current.isArchived),
  };

  return {
    previousSnapshot: currentSnapshot,
    nextRecord,
  };
};

export const buildMedicationVersionSnapshot = (entry = {}) => {
  const normalized = normalizeMedicationDetail(entry);
  return {
    id: normalized.id,
    name: normalized.name,
    genericName: normalized.genericName,
    brandName: normalized.brandName,
    aliases: normalized.aliases,
    notes: normalized.notes,
    dose: normalized.dose,
    unit: normalized.unit,
    time: normalized.time,
    form: normalized.form,
    category: normalized.category,
    schedules: normalized.schedules,
    startDate: normalized.startDate,
    isArchived: Boolean(normalized.isArchived),
    archivedAt: normalized.archivedAt || "",
    archivedBy: normalized.archivedBy || "",
    effectiveFrom: normalized.effectiveFrom || "",
    effectiveTo: normalized.effectiveTo || "",
    savedAt: normalized.savedAt || "",
    planHistory: normalized.planHistory || [],
    syncStatus: normalized.syncStatus || "draft",
  };
};

export const createMedicationSchedule = (overrides = {}) => ({
  id: overrides.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  dose: String(overrides.dose || "").trim(),
  unit: String(overrides.unit ?? "mg").trim(),
  time: String(overrides.time || getCurrentLocalTime()).trim(),
});

export const normalizeMedicationSchedule = normalizeMedicationScheduleQuantity;

export const MEDICATION_SCHEDULE_TYPE_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "every_x_hours", label: "Every X hours" },
  { value: "specific_days", label: "Specific days" },
  { value: "as_needed", label: "As needed" },
  { value: "occasionally", label: "Occasionally" },
  { value: "short_course", label: "Short course" },
];

export const MEDICATION_WEEKDAY_OPTIONS = [
  { value: "sun", label: "S" },
  { value: "mon", label: "M" },
  { value: "tue", label: "T" },
  { value: "wed", label: "W" },
  { value: "thu", label: "T" },
  { value: "fri", label: "F" },
  { value: "sat", label: "S" },
];

export const createMedicationDetail = (overrides = {}) => {
  const today = new Date().toISOString().slice(0, 10);
  const form = overrides.form || "pill";
  const schedules = Array.isArray(overrides.schedules) && overrides.schedules.length
    ? overrides.schedules.map((schedule, index) => normalizeMedicationSchedule(
        schedule,
        `${overrides.id || "med"}-${index}`
      ))
    : [createMedicationSchedule({ dose: overrides.dose || "", unit: overrides.unit || "mg", time: overrides.time || "" })];
  const firstSchedule = schedules[0] || createMedicationSchedule();

  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: "",
    dose: firstSchedule.dose || "",
    unit: firstSchedule.unit || "mg",
    category: "prescription",
    notes: "",
    sourceDocuments: [],
    ...overrides,
    form,
    scheduleType: overrides.scheduleType || "scheduled",
    selectedDays: Array.isArray(overrides.selectedDays) ? overrides.selectedDays : [],
    intervalHours: String(overrides.intervalHours || "").trim(),
    intervalStartTime: String(overrides.intervalStartTime || "").trim(),
    courseStartDate: overrides.courseStartDate || overrides.startDate || today,
    courseEndDate: String(overrides.courseEndDate || "").trim(),
    courseDays: String(overrides.courseDays || "").trim(),
    asNeededReason: String(overrides.asNeededReason || "").trim(),
    maxFrequency: String(overrides.maxFrequency || "").trim(),
    schedules,
    startDate: overrides.startDate || today,
    isArchived: Boolean(overrides.isArchived),
    archivedAt: overrides.archivedAt || "",
    archivedBy: overrides.archivedBy || "",
    effectiveFrom: overrides.effectiveFrom || "",
    effectiveTo: overrides.effectiveTo || "",
    history: Array.isArray(overrides.history) ? overrides.history : [],
    planHistory: Array.isArray(overrides.planHistory) ? overrides.planHistory : [],
    providerId: String(overrides.providerId || "").trim(),
    syncStatus: overrides.syncStatus || "draft",
    savedAt: overrides.savedAt || "",
  };
};

export const normalizeMedicationDetail = (entry = {}, fallbackId = "") => {
  const canonical = readCanonicalMedicationRecord(entry);
  const canonicalMedication = canonical.medication || {};
  const canonicalSchedule = canonical.schedule || {};
  const name = String(canonicalMedication.name || entry.name || "").trim();
  const form = canonicalMedication.form || entry.form || "pill";
  const resolvedId = canonicalMedication.id || entry.id || fallbackId || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const canonicalTimes = Array.isArray(canonicalSchedule.times) ? canonicalSchedule.times : [];
  const sourceSchedules = canonicalTimes.length
    ? canonicalTimes.map((time, index) => ({ id: `${resolvedId}-${index}`, time }))
    : (Array.isArray(entry.schedules) ? entry.schedules : []);
  const schedules = sourceSchedules.length
    ? sourceSchedules.map((schedule, index) => normalizeMedicationSchedule(
        {
          ...schedule,
          dose: canonicalMedication.dose || schedule.dose,
          unit: canonicalMedication.unit || schedule.unit,
        },
        `${resolvedId}-${index}`
      ))
    : [normalizeMedicationSchedule({
        id: `${resolvedId}-0`,
        dose: canonicalMedication.dose || entry.dose || "",
        unit: canonicalMedication.unit || entry.unit || "",
        time: entry.time || "",
      }, `${resolvedId}-0`)];
  const firstSchedule = schedules[0] || createMedicationSchedule();

  return {
    id: resolvedId,
    name,
    dose: firstSchedule.dose || String(entry.dose || "").trim(),
    unit: firstSchedule.unit || String(entry.unit || "").trim(),
    doseText:firstSchedule.doseText || canonicalMedication.doseText || "",
    doseNeedsClarification:Boolean(canonicalMedication.doseNeedsClarification),
    time: firstSchedule.time || String(entry.time || "").trim(),
    form,
    genericName: String(canonicalMedication.genericName || entry.genericName || "").trim(),
    brandName: String(canonicalMedication.brandName || entry.brandName || "").trim(),
    aliases: Array.isArray(canonicalMedication.aliases) ? canonicalMedication.aliases : (Array.isArray(entry.aliases) ? entry.aliases : []),
    category: entry.category || "prescription",
    scheduleType: entry.scheduleType || "scheduled",
    selectedDays: Array.isArray(entry.selectedDays) ? entry.selectedDays : [],
    intervalHours: String(canonicalSchedule.intervalHours || entry.intervalHours || "").trim(),
    intervalStartTime: String(entry.intervalStartTime || "").trim(),
    courseStartDate: String(entry.courseStartDate || entry.startDate || "").trim(),
    courseEndDate: String(entry.courseEndDate || "").trim(),
    courseDays: String(canonicalSchedule.durationDays || entry.courseDays || "").trim(),
    asNeededReason: String(entry.asNeededReason || "").trim(),
    maxFrequency: String(entry.maxFrequency || "").trim(),
    purpose: String(entry.purpose || "").trim(),
    notes: String(entry.notes || "").trim(),
    prescribingDoctor: String(canonical.provider || entry.prescribingDoctor || "").trim(),
    providerId: String(entry.providerId || "").trim(),
    sourceScheduleText: String(canonicalSchedule.text || entry.sourceScheduleText || "").trim(),
    sourceStatus: String(entry.sourceStatus || "").trim(),
    needsReview: Boolean(entry.needsReview || canonicalMedication.doseNeedsClarification),
    reviewReasons: Array.isArray(entry.reviewReasons) ? entry.reviewReasons : [],
    schedules,
    startDate: String(entry.startDate || "").trim(),
    isArchived: Boolean(entry.isArchived),
    archivedAt: String(entry.archivedAt || "").trim(),
    archivedBy: String(entry.archivedBy || "").trim(),
    effectiveFrom: String(entry.effectiveFrom || "").trim(),
    effectiveTo: String(entry.effectiveTo || "").trim(),
    history: Array.isArray(entry.history) ? entry.history : [],
    planHistory: Array.isArray(entry.planHistory) ? entry.planHistory : [],
    sourceDocuments: Array.isArray(entry.sourceDocuments) ? entry.sourceDocuments : [],
    temporaryHold: entry.temporaryHold && typeof entry.temporaryHold === 'object'
      ? {
          startDate: String(entry.temporaryHold.startDate || '').trim(),
          endDate: String(entry.temporaryHold.endDate || '').trim(),
          resumeDate: String(entry.temporaryHold.resumeDate || '').trim(),
          reason: String(entry.temporaryHold.reason || '').trim(),
          sourceId: String(entry.temporaryHold.sourceId || '').trim(),
          sourceTitle: String(entry.temporaryHold.sourceTitle || '').trim(),
          dateConfirmed: Boolean(entry.temporaryHold.dateConfirmed),
      }
      : null,
    medicationPlanAction: String(canonical.action || entry.medicationPlanAction || '').trim(),
    completionReason: String(canonical.reason || entry.completionReason || '').trim(),
    completedCourse: entry.completedCourse && typeof entry.completedCourse === 'object'
      ? {
          effectiveDate: String(entry.completedCourse.effectiveDate || '').trim(),
          effectiveDateText: String(entry.completedCourse.effectiveDateText || '').trim(),
          reason: String(entry.completedCourse.reason || '').trim(),
          dateConfirmed: Boolean(entry.completedCourse.dateConfirmed),
      }
      : null,
    canonicalMedicationRecord: canonical,
    syncStatus: entry.syncStatus || "draft",
    savedAt: String(entry.savedAt || "").trim(),
  };
};

export const formatMedicationProviderLabel = (provider = {}) => {
  const name = String(provider.name || "").trim();
  const specialty = String(provider.specialty || "").trim();
  return [name, specialty].filter(Boolean).join(" · ");
};

export const resolveMedicationProviderDisplay = (entry = {}, providers = []) => {
  const providerId = String(entry.providerId || "").trim();
  if (providerId && Array.isArray(providers)) {
    const provider = providers.find((item) => item?.id === providerId) || null;
    if (provider) {
      return {
        providerId,
        provider,
        label: formatMedicationProviderLabel(provider),
        source: "provider",
      };
    }
  }

  const prescribingDoctor = String(entry.prescribingDoctor || "").trim();
  return {
    providerId,
    provider: null,
    label: prescribingDoctor || providerId,
    source: prescribingDoctor ? "prescribingDoctor" : (providerId ? "provider" : ""),
  };
};

export const summarizeMedicationDetail = (entry = {}) => {
 const normalized=normalizeMedicationDetail(entry);
 const categoryLabel=MEDICATION_CATEGORY_OPTIONS.find(option=>option.value===normalized.category)?.label || 'Other';
 return buildMedicationDetailSummary(normalized,categoryLabel);
};

const getVersionDateKey = (version = {}) => parseLocalDateKey(
  version.effectiveFrom
  || version.startDate
  || version.savedAt
  || version.createdAt
  || ""
);

const getWeekdayKeyForDate = (dateValue = new Date()) => {
  const date = coerceDate(dateValue) || new Date();
  return ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][date.getDay()];
};

const addDaysToDateKey = (dateKey = "", daysToAdd = 0) => {
  if (!dateKey) {
    return "";
  }

  const date = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  date.setDate(date.getDate() + daysToAdd);
  return parseLocalDateKey(date);
};

const getMedicationCourseEndDateKey = (medication = {}) => {
  const explicitEndDate = parseLocalDateKey(medication.courseEndDate || medication.effectiveTo || "");
  if (explicitEndDate) {
    return explicitEndDate;
  }

  const courseDays = Number(medication.courseDays || 0);
  const courseStartDate = parseLocalDateKey(medication.courseStartDate || medication.startDate || "");
  if (!courseDays || !courseStartDate) {
    return "";
  }

  return addDaysToDateKey(courseStartDate, Math.max(0, courseDays - 1));
};

const getMedicationCourseDayLabel = (medication = {}, dateKey = "") => {
  const courseDays = Number(medication.courseDays || 0);
  const courseStartDate = parseLocalDateKey(medication.courseStartDate || medication.startDate || "");
  if (!courseDays || !courseStartDate || !dateKey) {
    return "";
  }

  const start = new Date(`${courseStartDate}T12:00:00`);
  const target = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(target.getTime())) {
    return "";
  }

  const elapsed = Math.floor((target.getTime() - start.getTime()) / 86400000) + 1;
  if (elapsed < 1) {
    return "";
  }

  return `Day ${Math.min(elapsed, courseDays)} of ${courseDays}`;
};

const shouldShowMedicationOnDate = (medication = {}, dateKey = "", dateValue = new Date()) => {
  const scheduleType = String(medication.scheduleType || "scheduled").trim();
  if (medication.isArchived) {
    return false;
  }

  if (scheduleType === "specific_days") {
    const selectedDays = Array.isArray(medication.selectedDays) ? medication.selectedDays : [];
    return selectedDays.length === 0 || selectedDays.includes(getWeekdayKeyForDate(dateValue));
  }

  if (scheduleType === "short_course") {
    const startDate = parseLocalDateKey(medication.courseStartDate || medication.startDate || "");
    const endDate = getMedicationCourseEndDateKey(medication);
    if (startDate && dateKey < startDate) {
      return false;
    }
    if (endDate && dateKey > endDate) {
      return false;
    }
  }

  return true;
};

const getVersionEndDateKey = (version = {}) => parseLocalDateKey(
  version.effectiveTo
  || version.archivedAt
  || ""
);

const isVersionActiveOnDate = (version = {}, dateKey = "") => {
  if (!dateKey) {
    return true;
  }

  const startKey = getVersionDateKey(version);
  const endKey = getVersionEndDateKey(version);

  if (startKey && dateKey < startKey) {
    return false;
  }

  if (endKey && dateKey > endKey) {
    return false;
  }

  return true;
};

export const getActiveMedicationVersionForDate = (medication = {}, dateValue = new Date()) => {
  const normalized = normalizeMedicationDetail(medication);
  const dateKey = parseLocalDateKey(dateValue || new Date());
  const versions = [
    normalized,
    ...(Array.isArray(normalized.history) ? normalized.history : []),
  ]
    .filter(Boolean)
    .map((version, index) => normalizeMedicationDetail(version, `${normalized.id || "med"}-version-${index}`));

  const activeVersions = versions.filter((version) => isVersionActiveOnDate(version, dateKey));
  if (!activeVersions.length) {
    return normalized;
  }

  activeVersions.sort((left, right) => {
    const leftStart = getVersionDateKey(left);
    const rightStart = getVersionDateKey(right);
    if (leftStart !== rightStart) {
      return leftStart.localeCompare(rightStart);
    }

    return String(left.savedAt || left.createdAt || "").localeCompare(String(right.savedAt || right.createdAt || ""));
  });

  return activeVersions[activeVersions.length - 1];
};

export const buildMedicationDoseInstancesForDate = (medication = {}, dateValue = new Date()) => {
  const activeVersion = getActiveMedicationVersionForDate(medication, dateValue);
  const scheduledDateKey = parseLocalDateKey(dateValue || new Date());
  const scheduleType = activeVersion.scheduleType || "scheduled";
  if (!shouldShowMedicationOnDate(activeVersion, scheduledDateKey, dateValue)) {
    return [];
  }

  const schedules = Array.isArray(activeVersion.schedules) && activeVersion.schedules.length
    ? activeVersion.schedules
    : [createMedicationSchedule({
        dose: activeVersion.dose || "",
        unit: activeVersion.unit || "",
        time: "",
      })];

  const isPrnLike = ["as_needed", "occasionally"].includes(scheduleType);
  const isInterval = scheduleType === "every_x_hours";
  const resolvedSchedules = isPrnLike || isInterval
    ? [schedules[0] || createMedicationSchedule({ dose: activeVersion.dose || "", unit: activeVersion.unit || "", time: "" })]
    : schedules;

  return resolvedSchedules.map((schedule, scheduleIndex) => ({
    medicationId: activeVersion.id,
    medicationVersionId: activeVersion.id,
    medicationVersionSnapshot: buildMedicationVersionSnapshot(activeVersion),
    medicationName: activeVersion.name,
    medicationCategory: activeVersion.category || "prescription",
    medicationStatus: activeVersion.isArchived ? "stopped" : "active",
    scheduleId: schedule.id || `${activeVersion.id}-${scheduleIndex}`,
    scheduleIndex,
    scheduledDateKey,
    startDate: activeVersion.startDate || "",
    endDate: activeVersion.effectiveTo || "",
    scheduledTime: isPrnLike || isInterval ? "" : (schedule.time || ""),
    plannedDose: schedule.dose || activeVersion.dose || "",
    unit: schedule.unit || activeVersion.unit || "",
    form: activeVersion.form || "",
    scheduleType,
    selectedDays: Array.isArray(activeVersion.selectedDays) ? activeVersion.selectedDays : [],
    intervalHours: activeVersion.intervalHours || "",
    intervalStartTime: activeVersion.intervalStartTime || "",
    courseStartDate: activeVersion.courseStartDate || "",
    courseEndDate: activeVersion.courseEndDate || "",
    courseDays: activeVersion.courseDays || "",
    courseDayLabel: scheduleType === "short_course" ? getMedicationCourseDayLabel(activeVersion, scheduledDateKey) : "",
    asNeededReason: activeVersion.asNeededReason || "",
    maxFrequency: activeVersion.maxFrequency || "",
    purpose: activeVersion.purpose || "",
    notes: activeVersion.notes || "",
    medicationScheduleSnapshot: normalizeMedicationSchedule(schedule, schedule.id || `${activeVersion.id}-${scheduleIndex}`),
  }));
};
